import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2 } from 'lucide-react';
import { formatLastFirst, getDuplicateNameKeys } from '../../lib/nameFormat';

const BUNDESLAENDER = [
  { value: 'BW', label: 'Baden-Württemberg' },
  { value: 'BY', label: 'Bayern' },
  { value: 'BE', label: 'Berlin' },
  { value: 'BB', label: 'Brandenburg' },
  { value: 'HB', label: 'Bremen' },
  { value: 'HH', label: 'Hamburg' },
  { value: 'HE', label: 'Hessen' },
  { value: 'MV', label: 'Mecklenburg-Vorpommern' },
  { value: 'NI', label: 'Niedersachsen' },
  { value: 'NW', label: 'Nordrhein-Westfalen' },
  { value: 'RP', label: 'Rheinland-Pfalz' },
  { value: 'SL', label: 'Saarland' },
  { value: 'SN', label: 'Sachsen' },
  { value: 'ST', label: 'Sachsen-Anhalt' },
  { value: 'SH', label: 'Schleswig-Holstein' },
  { value: 'TH', label: 'Thüringen' },
];

export const BranchFormDialog = ({
  open, onClose, editItem, form, setForm,
  onSave, saving, managerEmployees,
}) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-heading">{editItem ? 'Niederlassung bearbeiten' : 'Neue Niederlassung'}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 pt-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Niederlassung Nord" data-testid="branch-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Kürzel</Label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="NL-001" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Adresse</Label>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Musterstr. 1" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>PLZ</Label>
            <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
          </div>
          <div className="space-y-2 col-span-2">
            <Label>Stadt</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Bundesland</Label>
          <Select value={form.bundesland || "none"} onValueChange={(v) => setForm({ ...form, bundesland: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="branch-bundesland-select"><SelectValue placeholder="Bundesland wählen" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Kein Bundesland</SelectItem>
              {BUNDESLAENDER.map((bl) => <SelectItem key={bl.value} value={bl.value}>{bl.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Telefon</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>E-Mail</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Niederlassungsleiter/in</Label>
          <Select value={form.manager_id || "none"} onValueChange={(v) => setForm({ ...form, manager_id: v === "none" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Kein Leiter</SelectItem>
              {managerEmployees.length > 0 ? (
                (() => {
                  const dk = getDuplicateNameKeys(managerEmployees);
                  return managerEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{formatLastFirst(emp, dk)}</SelectItem>
                  ));
                })()
              ) : (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">Keine aktiven Mitarbeiter vorhanden</div>
              )}
            </SelectContent>
          </Select>
          {managerEmployees.length === 0 && (
            <p className="text-xs text-muted-foreground">Erstellen Sie zuerst einen Mitarbeiter.</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
          <Label>Aktiv</Label>
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
          <Button onClick={onSave} disabled={saving} className="rounded-full" data-testid="save-branch-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editItem ? 'Aktualisieren' : 'Erstellen')}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
